import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { postData } from "../../Webservice/Webservice";
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { saveAuthData, getReturnPath } from "../../Utils/authHelper";
import CustomerSignInPanel from "./CustomerSignInPanel";
import EmailLoginForm from "./EmailLoginForm";
import BackToHome from "./BackToHome";
import { SwapTabs, SwapPanes } from "./SwapUI";
import { floatA, floatB, pulse, growLine, shake, stagger, pageSx, orbSx, sparkSx, cardSx, badgeSx, fieldSx, goldButtonSx } from "./signinStyles";

// One page for both kinds of account: a Customer | Business swap at the top.
// mode only chooses which one is open first: "customer" at /signin, "business" at /business/signin.
function Signin({ mode = "customer" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [accountType, setAccountType] = useState(mode === "business" ? "business" : "customer");
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetEmailError, setResetEmailError] = useState("");
  const [apiError, setApiError] = useState("");

  const isCustomer = accountType === "customer";

  // swapping keeps the address in step without leaving the page
  const handleSwapAccount = (type) => {
    setAccountType(type);
    setApiError("");
    window.history.replaceState(null, "", type === "business" ? "/business/signin" : "/signin");
  };

  // signed in with email, mobile OTP or Google
  const handleSignedIn = (res) => {
    setApiError("");
    saveAuthData(res.token, res.customer, "common");
    // not navigate(-1): that goes back to the sign up page when the visitor came from there
    navigate(getReturnPath(location.state), { replace: true });
  };

  const handleSendResetLink = async () => {
    setApiError("");
    if (!resetEmail.trim()) {
      setResetEmailError("Email is required");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(resetEmail)) {
      setResetEmailError("Please enter a valid email address");
      return;
    }
    setResetEmailError("");
    setResetLoading(true);
    try {
      const response = await postData("customer/forgot-password", {
        email: resetEmail.trim(),
        customerType: accountType,
      });
      if (response && !response.error) {
        toast.success("Password reset link sent to " + resetEmail.trim());
        setResetEmail("");
        setIsResetPassword(false);
      } else {
        setApiError(response?.message || "Failed to send reset link. Please try again.");
      }
    } catch (error) {
      setApiError(error.message || "Failed to send reset link. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleBackToSignIn = () => {
    setIsResetPassword(false);
    setResetEmail("");
    setResetEmailError("");
    setApiError("");
  };

  return (
    <Box sx={pageSx}>
      <BackToHome />
      {/* drifting light behind the card */}
      <Box sx={{ ...orbSx, width: 420, height: 420, top: "-120px", left: "-100px", background: "radial-gradient(circle, rgba(200,160,84,0.55), transparent 68%)", animation: `${floatA} 14s ease-in-out infinite` }} />
      <Box sx={{ ...orbSx, width: 520, height: 520, bottom: "-180px", right: "-140px", background: "radial-gradient(circle, rgba(244,233,201,0.35), transparent 68%)", animation: `${floatB} 18s ease-in-out infinite` }} />
      <Box sx={{ ...orbSx, width: 260, height: 260, top: "55%", left: "62%", background: "radial-gradient(circle, rgba(200,160,84,0.30), transparent 70%)", animation: `${floatA} 11s ease-in-out infinite reverse` }} />
      {[0, 1, 2, 3, 4, 5].map((n) => (
        <Box key={n} sx={{ ...sparkSx, left: `${12 + n * 15}%`, animationDelay: `${n * 1.3}s`, animationDuration: `${7 + (n % 3) * 2}s` }} />
      ))}

      {/* the card */}
      <Box sx={cardSx}>
        <Box sx={{ ...stagger(0.1), textAlign: "center", mb: 1 }}>
          <Box
            component="img"
            src="/img/tvfmlogo1.png"
            alt="TVFM Logo"
            sx={{ height: 44, width: "auto", cursor: "pointer", animation: `${pulse} 4s ease-in-out infinite` }}
            onClick={() => navigate("/")}
          />
        </Box>

        {/* Customer | Business, swapped on this same page */}
        {!isResetPassword && (
          <Box sx={stagger(0.15)}>
            <SwapTabs
              value={accountType}
              onChange={handleSwapAccount}
              options={[
                { value: "customer", label: "Customer", icon: <PersonIcon sx={{ fontSize: 18 }} /> },
                { value: "business", label: "Business", icon: <BusinessIcon sx={{ fontSize: 18 }} /> },
              ]}
            />
          </Box>
        )}

        <Box key={`${accountType}-${isResetPassword}`} sx={{ ...stagger(0.05), textAlign: "center", mb: 1.75 }}>
          {isResetPassword && (
            <Box sx={{ ...badgeSx, bgcolor: isCustomer ? "#fbf3df" : "#ececec", color: isCustomer ? "#8a6a24" : "#333", display: "inline-flex", alignItems: "center", gap: 0.6 }}>
              {isCustomer ? <PersonIcon sx={{ fontSize: 14 }} /> : <BusinessIcon sx={{ fontSize: 14 }} />}
              {isCustomer ? "Customer" : "Business"}
            </Box>
          )}
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#111", mt: isResetPassword ? 1 : 0.5 }}>
            {isResetPassword ? "Forgot Password" : isCustomer ? "Welcome back" : "Business Sign In"}
          </Typography>
          <Box sx={{ width: 44, height: 3, borderRadius: 2, bgcolor: "#C8A054", mx: "auto", mt: 1, animation: `${growLine} 0.8s 0.2s both ease-out` }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8, fontSize: "0.82rem" }}>
            {isResetPassword
              ? "Enter your email to receive a reset link"
              : isCustomer
              ? "Sign in to shop your favourites"
              : "Sign in to your business account"}
          </Typography>
        </Box>

        {apiError && (
          <Typography key={apiError} color="error" variant="body2" align="center" sx={{ mb: 2, bgcolor: "#ffebee", p: 1, borderRadius: 2, animation: `${shake} 0.45s ease` }}>
            {apiError}
          </Typography>
        )}

        <Box key={isResetPassword ? "reset" : "signin"} sx={stagger(0.2)}>
          {!isResetPassword ? (
            <SwapPanes index={isCustomer ? 0 : 1}>
              {/* section 1: customer (Google, mobile OTP, email) */}
              <CustomerSignInPanel onSignedIn={handleSignedIn} onError={setApiError}>
                <EmailLoginForm
                  accountType="customer"
                  onSignedIn={handleSignedIn}
                  onError={setApiError}
                  onForgotPassword={() => setIsResetPassword(true)}
                />
              </CustomerSignInPanel>

              {/* section 2: business (email and password) */}
              <EmailLoginForm
                accountType="business"
                onSignedIn={handleSignedIn}
                onError={setApiError}
                onForgotPassword={() => setIsResetPassword(true)}
              />
            </SwapPanes>
          ) : (
            /* ── Forgot Password Form ── */
            <Box>
              <TextField
                fullWidth
                placeholder="Email address"
                inputProps={{ "aria-label": "Email address" }}
                type="email"
                size="small"
                value={resetEmail}
                onChange={(e) => {
                  setResetEmail(e.target.value);
                  if (resetEmailError) setResetEmailError("");
                }}
                variant="outlined"
                margin="dense"
                autoFocus
                error={!!resetEmailError}
                helperText={resetEmailError}
                sx={fieldSx}
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: "#C8A054", fontSize: 20 }} /></InputAdornment> }}
              />

              <Button fullWidth variant="contained" onClick={handleSendResetLink} disabled={resetLoading} startIcon={resetLoading ? null : <SendIcon />} sx={{ ...goldButtonSx, mt: 1.5 }}>
                {resetLoading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Send Reset Link"}
              </Button>

              <Typography variant="body2" align="center" sx={{ mt: 2.5 }}>
                <Link component="button" type="button" variant="body2" onClick={handleBackToSignIn} underline="hover" sx={{ color: "#8a6a24", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  <ArrowBackIcon sx={{ fontSize: 17 }} />
                  Back to Sign In
                </Link>
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default Signin;
